import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type { AdminAccount, AdminAccountProfile, AdminRole, FirstTouch } from "../api/types.js";
import { count, trx, utc } from "../format.js";
import { useRememberedChoice } from "../lib/remembered-choice.js";
import type { Router } from "../router.js";
import {
  Address,
  Blank,
  Button,
  CopyButton,
  ErrorNote,
  Note,
  Panel,
  Pill,
  ScreenHeader,
  SignInMethods,
  Spinner,
  Table,
  Td,
  Th,
  Tile,
  TxId,
  inputClass,
} from "../ui.js";
import { DepositAddressPanel, LedgerAdjustmentForm, LedgerPanel } from "./accounts.js";

/**
 * One customer on one screen (docs/product/mocks/admin-customer-profile-mock.html, owner-approved
 * 2026-10-11), opened from a row on Accounts. One read, `GET /v1/admin/accounts/{id}/profile` (viewer);
 * the note is the one write here, `PATCH /v1/admin/accounts/{id}/note` (editor, audited). The ledger,
 * deposit-address and adjustment panels are the Accounts screen's own, reused as they are.
 */
const NOTE_MAX = 2000;
const ORDER_FILTERS = ["all", "completed", "active", "failed"] as const;
type OrderFilter = (typeof ORDER_FILTERS)[number];
type Panel = "ledger" | "deposit" | "adjust" | null;
type Credit = AdminAccountProfile["deposits"][number]["how"];

/** How the first money came, in the operator's words (the API keeps its short machine values). */
const CAME_LABEL: Record<Credit, string> = {
  "wallet payment": "Paid from their wallet (widget)",
  "deposit address": "Topped up the dashboard balance",
};
const CAME_NOTE: Record<Credit, string> = {
  "wallet payment": "A direct TRX transfer to our payment address; the account was created automatically.",
  "deposit address": "A transfer to their personal deposit address.",
};
/** The deposits table's "How" column. */
const HOW_LABEL: Record<Credit, string> = {
  "wallet payment": "Wallet payment (widget)",
  "deposit address": "Dashboard top-up",
};
/** Deposits under 0.1 TRX are hidden by default: dust, usually address-poisoning spam. */
const DUST_BELOW_SUN = 100_000;

const PILL_TONE: Record<"completed" | "active" | "failed", "good" | "attention" | "bad"> = { completed: "good", active: "attention", failed: "bad" };

function ago(iso: string | null): string | null {
  if (iso === null) return null;
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} days ago`;
}

function FactCard({ label, value, sub }: { label: string; value: string | null; sub?: string | null }) {
  return (
    <div class="min-w-0 rounded-[14px] border border-line bg-surface px-3 py-2.5">
      <div class="num text-[10.5px] tracking-[0.06em] text-ink-muted uppercase">{label}</div>
      <div class="num mt-0.5 text-[12.5px] text-ink">{value ?? <Blank />}</div>
      {sub ? <div class="text-[11px] text-ink-muted">{sub}</div> : null}
    </div>
  );
}

/** One line on where the customer came from, e.g. "Came from google.com · utm: google/cpc · landed on /ru/". */
export function firstTouchLine(touch: FirstTouch | null): string {
  if (touch === null) return "Came from: unknown (before 2026-10-11)";
  const parts = [touch.ref_host ? `Came from ${touch.ref_host}` : "Came directly (no referrer)"];
  const utm = [touch.utm_source, touch.utm_medium, touch.utm_campaign, touch.utm_term].filter(Boolean);
  if (utm.length > 0) parts.push(`utm: ${utm.join("/")}`);
  if (touch.landing_path) parts.push(`landed on ${touch.landing_path}`);
  return parts.join(" · ");
}

function asAccount(profile: AdminAccountProfile): AdminAccount {
  const a = profile.account;
  return {
    id: a.id,
    brand_id: a.brand_id,
    network: a.network,
    owner_address: a.owner_address,
    ...(a.sign_in === undefined ? {} : { sign_in: a.sign_in }),
    status: a.status,
    balance_sun: profile.money.balance_sun,
    reserved_sun: profile.money.reserved_sun,
    created_at: a.created_at,
  };
}

export function AccountProfileScreen({ id, role, router }: { id: string; role: AdminRole; router: Router }) {
  const profile = useResource(
    (signal) => {
      void signal;
      return adminReads.accountProfile(id);
    },
    [id],
  );
  const [panel, setPanel] = useState<Panel>(null);

  const back = (
    <a
      href="/accounts"
      onClick={(event) => router.onNavigate(event as MouseEvent, "accounts")}
      class="num text-[11px] text-accent-text hover:underline"
    >
      Accounts
    </a>
  );

  if (profile.loading && profile.data == null) return <Spinner />;
  if (profile.error !== null) return <ErrorNote error={profile.error} onRetry={() => profile.reload()} />;
  const view = profile.data;
  if (view == null) return <Spinner />;
  const a = view.account;
  const m = view.money;
  const account = asAccount(view);
  const credit = a.came_by.first_credit;
  const cameBy = a.came_by.compat_dialects.length > 0
    ? `Uses the API (${a.came_by.compat_dialects.join(", ")} format)`
    : a.came_by.has_api_keys
      ? "Uses the API"
      : credit === null
        ? null
        : CAME_LABEL[credit];

  return (
    <>
      <div class="mb-2 text-[11px] text-ink-muted">
        {back} / customer
      </div>
      <ScreenHeader
        title={a.owner_address ?? a.sign_in?.find((m) => m.value !== null)?.value ?? a.id}
        lead={`account ${a.id} · ${a.status}`}
        action={
          <span class="inline-flex flex-wrap gap-1.5">
            {a.owner_address === null ? null : <CopyButton value={a.owner_address} label="copy address" />}
            {role === "editor" ? (
              <Button tone="ghost" onClick={() => setPanel(panel === "adjust" ? null : "adjust")}>
                Adjust ledger
              </Button>
            ) : null}
            <Button tone="ghost" onClick={() => setPanel(panel === "deposit" ? null : "deposit")}>
              Deposit address
            </Button>
            <Button tone="ghost" onClick={() => setPanel(panel === "ledger" ? null : "ledger")}>
              Ledger journal
            </Button>
          </span>
        }
      />

      <p class="mb-3 text-[12px] text-ink-muted" title={a.first_touch?.at ? `first seen ${utc(a.first_touch.at)}` : undefined}>
        {firstTouchLine(a.first_touch)}
      </p>
      {(a.sign_in ?? []).length > 0 ? (
        <div class="mb-3 flex flex-wrap items-baseline gap-2 text-[12px]">
          <span class="text-ink-muted">Sign-in:</span>
          <SignInMethods methods={a.sign_in!} detailed />
        </div>
      ) : null}

      {panel === "adjust" ? (
        <div class="mb-3">
          <Panel>
            <LedgerAdjustmentForm
              account={account}
              onDone={() => {
                setPanel(null);
                profile.reload();
              }}
              onCancel={() => setPanel(null)}
            />
          </Panel>
        </div>
      ) : panel === "deposit" ? (
        <div class="mb-3">
          <DepositAddressPanel account={account} role={role} onClose={() => setPanel(null)} />
        </div>
      ) : panel === "ledger" ? (
        <div class="mb-3">
          <LedgerPanel account={account} onClose={() => setPanel(null)} />
        </div>
      ) : null}

      <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <FactCard label="Created" value={utc(a.created_at)} sub={ago(a.created_at)} />
        <FactCard
          label="First deposit"
          value={a.first_deposit_at === null ? null : utc(a.first_deposit_at)}
          sub={a.first_deposit_sun === null ? null : `${trx(a.first_deposit_sun)}${credit === null ? "" : `, ${HOW_LABEL[credit].toLowerCase()}`}`}
        />
        <FactCard label="First order" value={a.first_order_at === null ? null : utc(a.first_order_at)} />
        <FactCard label="Last order" value={a.last_order_at === null ? null : utc(a.last_order_at)} sub={ago(a.last_order_at)} />
        <FactCard
          label="How they came"
          value={cameBy}
          sub={a.came_by.has_api_keys ? (credit === null ? null : CAME_LABEL[credit]) : "No API keys"}
        />
        <FactCard
          label="First money by"
          value={credit === null ? null : CAME_LABEL[credit]}
          sub={credit === null ? null : CAME_NOTE[credit]}
        />
      </div>

      <section class="mt-6 mb-7">
        <div class="mb-2.5 flex flex-wrap items-baseline gap-2.5">
          <h2 class="font-display text-[16px] font-extrabold">Money</h2>
          <p class="text-[12px] text-ink-muted">All in TRX. Balance is what we hold for them right now.</p>
        </div>
        <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <Tile label="Balance now" value={trx(m.balance_sun)} note={`${trx(m.reserved_sun)} reserved for active orders`} />
          <Tile label="Total deposited" value={trx(m.deposited_sun)} note={`${count(m.deposits)} deposits`} />
          <Tile label="Total spent" value={trx(m.spent_sun)} note={`${count(m.charged_orders)} order charges`} />
          <Tile label="Refunds" value={trx(m.refunded_sun)} note={`${count(m.refunds)} refunds`} />
          <Tile
            label="Average order"
            value={m.average_order_sun === null ? null : trx(m.average_order_sun)}
            note={m.average_order_sun === null ? "No order charged yet" : "Spent ÷ order charges"}
          />
        </div>
      </section>

      <DepositsPanel view={view} />

      <OrdersPanel view={view} router={router} />

      <div class="mb-7">
        <Panel title="Receivers" subtitle="Addresses they buy for, most orders first.">
          <Table
            rows={view.receivers.length}
            empty="No order yet, so no receiver."
            head={
              <tr>
                <Th>Address</Th>
                <Th numeric>Orders</Th>
                <Th numeric>Spent</Th>
                <Th>Last order</Th>
              </tr>
            }
          >
            {view.receivers.map((row) => (
              <tr key={row.receiver}>
                <Td mono>
                  <Address value={row.receiver} />
                  {row.own_wallet ? <span class="block text-[11px] text-ink-muted">their own wallet</span> : null}
                </Td>
                <Td numeric>{count(row.orders)}</Td>
                <Td numeric>{trx(row.spent_sun)}</Td>
                <Td mono>{utc(row.last_order_at)}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>

      <IntegrationSection view={view} />

      <NotePanel id={a.id} role={role} note={view.note} onSaved={() => profile.reload()} />
    </>
  );
}

function DepositsPanel({ view }: { view: AdminAccountProfile }) {
  const [showDust, setShowDust] = useState(false);
  const isDust = (row: AdminAccountProfile["deposits"][number]) => row.amount_sun < DUST_BELOW_SUN && row.usdt_amount === null;
  const dust = view.deposits.filter(isDust).length;
  const rows = showDust ? view.deposits : view.deposits.filter((row) => !isDust(row));
  return (
    <div class="mb-7">
      <Panel
        title="Deposits"
        subtitle="Money that came in, newest first. The totals above count every deposit, dust included."
        action={
          dust === 0 ? null : (
            <label class="num inline-flex cursor-pointer items-center gap-1.5 text-[11px] text-ink-muted">
              <input
                type="checkbox"
                checked={showDust}
                onChange={(event) => setShowDust((event.currentTarget as HTMLInputElement).checked)}
              />
              Show dust (&lt; 0.1 TRX), {count(dust)} hidden
            </label>
          )
        }
      >
        <Table
          rows={rows.length}
          empty={
            view.deposits.length > 0
              ? "Every deposit so far is dust, under 0.1 TRX. Tick the box above to see them."
              : "No deposit yet. A dashboard top-up or a wallet payment from the widget would appear here."
          }
          head={
            <tr>
              <Th>Time</Th>
              <Th numeric>Amount</Th>
              <Th>From</Th>
              <Th>How</Th>
              <Th numeric>Block</Th>
              <Th>Tx</Th>
            </tr>
          }
        >
          {rows.map((row) => (
            <tr key={row.journal_id}>
              <Td mono>{utc(row.created_at)}</Td>
              <Td numeric>
                {trx(row.amount_sun)}
                {row.usdt_amount === null ? null : <span class="block text-[11px] text-ink-muted">{row.usdt_amount} USDT</span>}
              </Td>
              <Td mono>{row.from === null ? <Blank /> : <Address value={row.from} />}</Td>
              <Td>{HOW_LABEL[row.how]}</Td>
              <Td numeric>{row.block_number ?? <Blank />}</Td>
              <Td>{row.txid === null ? <Blank /> : <TxId value={row.txid} />}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

function OrdersPanel({ view, router }: { view: AdminAccountProfile; router: Router }) {
  const [filter, setFilter] = useRememberedChoice<OrderFilter>("profile.orders.status", ORDER_FILTERS, "all");
  const rows = view.orders.data.filter((row) => filter === "all" || row.status_group === filter);
  const shown = view.orders.data.length;
  return (
    <div class="mb-7">
      <Panel
        title="Orders"
        subtitle={
          shown < view.orders.counts.all
            ? `Newest ${count(shown)} of ${count(view.orders.counts.all)}; counts are over all of them, the filter over the rows shown.`
            : "Newest first."
        }
        action={
          <div class="inline-flex flex-wrap rounded-[10px] bg-surface-raised p-0.5" role="group" aria-label="Status filter">
            {ORDER_FILTERS.map((key) => (
              <button
                key={key}
                type="button"
                aria-pressed={filter === key}
                onClick={() => setFilter(key)}
                class={`num rounded-[8px] px-2.5 py-1 text-[11px] ${filter === key ? "bg-surface text-ink" : "text-ink-muted"}`}
              >
                {key} {count(view.orders.counts[key])}
              </button>
            ))}
          </div>
        }
      >
        <Table
          rows={rows.length}
          empty={filter === "all" ? "No order yet." : `No ${filter} order among the rows shown.`}
          head={
            <tr>
              <Th>Time</Th>
              <Th>Status</Th>
              <Th numeric>Amount</Th>
              <Th>Tier</Th>
              <Th>Receiver</Th>
              <Th numeric>Price</Th>
              <Th numeric>Paid</Th>
              <Th>Filled by</Th>
              <Th>Tx</Th>
              <Th>{""}</Th>
            </tr>
          }
        >
          {rows.map((row) => (
            <tr key={row.id}>
              <Td mono>{utc(row.created_at)}</Td>
              <Td>
                <Pill value={row.status_label} tone={PILL_TONE[row.status_group]} />
                {row.failure === null ? null : <span class="block max-w-[220px] truncate text-[11px] text-ink-muted" title={row.failure}>{row.failure}</span>}
              </Td>
              <Td numeric>
                {count(row.amount)} {row.resource}
              </Td>
              <Td>{row.tier ?? <Blank />}</Td>
              <Td mono>
                <Address value={row.receiver} />
              </Td>
              <Td numeric>{row.price_sun_per_unit === null ? <Blank /> : `${row.price_sun_per_unit} SUN`}</Td>
              <Td numeric>{trx(row.paid_sun)}</Td>
              <Td>{row.filled_by ?? <Blank title="No allocation recorded" />}</Td>
              <Td>{row.txid === null ? <Blank /> : <TxId value={row.txid} />}</Td>
              <Td>
                <a
                  href={`/orders/${encodeURIComponent(row.id)}`}
                  onClick={(event) => router.onNavigate(event as MouseEvent, "orders", row.id)}
                  class="text-[11.5px] font-medium text-accent-text hover:underline"
                >
                  open
                </a>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}

function IntegrationSection({ view }: { view: AdminAccountProfile }) {
  const i = view.integration;
  return (
    <section class="mb-7">
      <div class="mb-2.5 flex flex-wrap items-baseline gap-2.5">
        <h2 class="font-display text-[16px] font-extrabold">Integration</h2>
        <p class="text-[12px] text-ink-muted">How their software talks to us. Secrets are never shown.</p>
      </div>
      <div class="grid gap-3 lg:grid-cols-2">
        <Panel title="API keys">
          <Table
            rows={i.api_keys.length}
            empty="No API key: they use the dashboard or the widget."
            head={
              <tr>
                <Th>Label</Th>
                <Th>Created</Th>
                <Th>Last used</Th>
                <Th>Scopes</Th>
                <Th>Format</Th>
              </tr>
            }
          >
            {i.api_keys.map((key, n) => (
              <tr key={`${key.label}-${n}`}>
                <Td>
                  {key.label}
                  {key.active ? null : <span class="block text-[11px] text-ink-muted">revoked or off</span>}
                </Td>
                <Td mono>{utc(key.created_at)}</Td>
                <Td mono>{key.last_used_at === null ? <Blank title="Never used" /> : ago(key.last_used_at)}</Td>
                <Td>{key.scopes.join(", ")}</Td>
                <Td>{key.compat_dialect ?? "our own API"}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
        <Panel title="Webhooks">
          <Table
            rows={i.webhooks.length}
            empty="No webhook registered."
            head={
              <tr>
                <Th>Host</Th>
                <Th>Events</Th>
                <Th>Last delivery</Th>
              </tr>
            }
          >
            {i.webhooks.map((hook) => (
              <tr key={hook.role}>
                <Td mono>
                  {hook.host ?? <Blank />}
                  <span class="block text-[11px] text-ink-muted">
                    {hook.role}, {hook.format}
                    {hook.active ? "" : ", off"}
                  </span>
                </Td>
                <Td>{hook.events === null || hook.events.length === 0 ? "all" : hook.events.join(", ")}</Td>
                <Td>
                  {hook.last_delivery_status === null ? (
                    <Blank title="Nothing delivered yet" />
                  ) : (
                    <Pill
                      value={String(hook.last_delivery_status)}
                      tone={hook.last_delivery_status >= 200 && hook.last_delivery_status < 300 ? "good" : "bad"}
                    />
                  )}{" "}
                  {ago(hook.last_delivery_at) ?? ""}
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
        <Panel
          title="Compatible API, last 7 days"
          subtitle="Orders created through a venue's format. Calls themselves are not recorded anywhere, so there is no call or error count."
        >
          <Table
            rows={i.compat_orders_7d.length}
            empty="No order through a compatible API in the last 7 days."
            head={
              <tr>
                <Th>Format</Th>
                <Th numeric>Orders</Th>
              </tr>
            }
          >
            {i.compat_orders_7d.map((row) => (
              <tr key={row.dialect}>
                <Td>{row.dialect}</Td>
                <Td numeric>{count(row.orders)}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
        <Panel title="Node keys">
          <Table
            rows={i.node_keys.length}
            empty="No node key."
            head={
              <tr>
                <Th>Label</Th>
                <Th>Created</Th>
                <Th>Status</Th>
                <Th numeric>Daily limit</Th>
              </tr>
            }
          >
            {i.node_keys.map((key, n) => (
              <tr key={`${key.created_at}-${n}`}>
                <Td>{key.label ?? <Blank />}</Td>
                <Td mono>{utc(key.created_at)}</Td>
                <Td>
                  <Pill value={key.active ? "active" : "off"} tone={key.active ? "good" : "neutral"} />
                </Td>
                <Td numeric>
                  {key.daily_limit_sun === null ? "none" : trx(key.daily_limit_sun)}
                  <span class="block text-[11px] text-ink-muted">over it: {key.limit_action.replace("_", " ")}</span>
                </Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>
    </section>
  );
}

function NotePanel({
  id,
  role,
  note,
  onSaved,
}: {
  id: string;
  role: AdminRole;
  note: AdminAccountProfile["note"];
  onSaved(): void;
}) {
  const [text, setText] = useState(note.text ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const editor = role === "editor";
  const changed = text !== (note.text ?? "");
  const tooLong = text.length > NOTE_MAX;
  return (
    <Panel title="Notes" subtitle="Only admins see this: who the customer is, how we know them, agreements.">
      <textarea
        class={`${inputClass} min-h-24 w-full resize-y leading-relaxed`}
        placeholder={editor ? "Who is this customer?" : "No note yet."}
        readOnly={!editor}
        aria-label="Note on this customer"
        value={text}
        onInput={(event) => setText((event.currentTarget as HTMLTextAreaElement).value)}
      />
      {error === null ? null : <ErrorNote error={error} />}
      <div class="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span class={`text-[11px] ${tooLong ? "text-danger" : "text-ink-muted"}`}>
          {note.updated_at === null ? "Never saved" : `Last saved ${utc(note.updated_at)}`}
          {note.updated_by === null ? "" : ` by ${note.updated_by}`} · {count(text.length)} / {count(NOTE_MAX)}
        </span>
        {editor ? (
          <Button
            disabled={busy || !changed || tooLong}
            onClick={() => {
              setBusy(true);
              setError(null);
              adminWrites
                .saveAccountNote(id, { note: text.trim() === "" ? null : text })
                .then(() => onSaved())
                .catch((cause: unknown) => setError(cause))
                .finally(() => setBusy(false));
            }}
          >
            Save
          </Button>
        ) : (
          <Note>Editors can change the note.</Note>
        )}
      </div>
    </Panel>
  );
}
